/**
 * Build-time brand switch for white-label deploys (CRA: set `REACT_APP_BRAND`).
 * Default `divyfi` keeps existing product naming and assets unchanged.
 */

export type BrandId = 'divyfi' | 'pgsplitter';

function normalizeBrandId(raw: string | undefined): BrandId {
  const v = raw?.trim().toLowerCase();
  if (v === 'pgsplitter' || v === 'pg_splitter' || v === 'pg-splitter') return 'pgsplitter';
  return 'divyfi';
}

export const brandId = normalizeBrandId(process.env.REACT_APP_BRAND);

export interface BrandFooterPartnerLine {
  beforeLink: string;
  linkHref: string;
  linkLabel: string;
}

export interface BrandConfig {
  id: BrandId;
  daisyTheme: string;
  storageNamespace: string;
  displayName: string;
  htmlTitle: string;
  metaDescription: string;
  logoHeaderSrc: string;
  logoHeaderAlt: string;
  heroLogoSrc: string;
  heroLogoAlt: string;
  /** Header / hero images use full rounding (e.g. circular mascot). */
  logoRoundedFull: boolean;
  wagmiAppName: string;
  lifiIntegrator: string;
  nav: { secondaryLabel: string; secondaryHref: string };
  docsNav?: { label: string; href: string };
  footer: {
    copyright: string;
    linkHref: string;
    linkLabel: string;
    /** When set (pgSplitter), footer shows only this partner / audit line. */
    singlePartnerLine?: BrandFooterPartnerLine;
  };
  dashboard: {
    /** If null, hero omits the large title (copy-only hero). */
    heroTitle: string | null;
    heroSubtitle: string;
    docLinkLabel: string;
    connectedSubtitle: string;
    marketingEvmsTitle: string;
    marketingEvmsBody: string;
    marketingGasTitle: string;
    marketingGasBody: string;
    bottomCta: string;
  };
  /** Pulse-first marketing layout: gas card first, SVG icons in titles. */
  marketingGasCardFirst?: boolean;
  libertySwapBanner?: { href: string; label: string };
  ecosystemBlurb?: { linkLabel: string; linkHref: string };
  gasEstimator: { splitterDeployLabel: string };
  /** Shown on whitelabel deploys (e.g. attribution to DivyFi). */
  poweredBy?: { href: string; linkLabel: string };
  /** On Create split, prefer switching the wallet to this chain when connected. */
  preferredChainId?: number;
}

const divyfi: BrandConfig = {
  id: 'divyfi',
  daisyTheme: 'mydark',
  storageNamespace: 'divyfi',
  displayName: 'DivyFi',
  htmlTitle: 'DivyFi',
  metaDescription: 'DivyFi — multi-chain revenue splits, deploy and claim.',
  logoHeaderSrc: '/logotext.png',
  logoHeaderAlt: 'DivyFi',
  heroLogoSrc: '/FullLogo.png',
  heroLogoAlt: 'DivyFi',
  logoRoundedFull: false,
  wagmiAppName: 'DivyFi',
  lifiIntegrator: 'DivyFi',
  nav: {
    secondaryLabel: 'Docs',
    secondaryHref: 'https://docs.divyfi.xyz',
  },
  footer: {
    copyright: 'DivyFi 2026',
    linkHref: 'https://proofsec.xyz',
    linkLabel: 'proofsec.xyz',
  },
  dashboard: {
    heroTitle: 'Deploy Multi-Chain Smart Contracts in One Click',
    heroSubtitle:
      'Deploy splitter and other smart contracts across multiple blockchains with automatic gas estimation, cross-chain conversion, and unified execution tracking. Manage balances and activity across all deployments from a single dashboard.',
    docLinkLabel: 'Documentation',
    connectedSubtitle: 'Your EVM splits and activity.',
    marketingEvmsTitle: 'EVM factories',
    marketingEvmsBody:
      'Splits indexed by the factory: see contracts where you are a payee, with native and token claim history.',
    marketingGasTitle: 'Gas & funding',
    marketingGasBody: 'Estimate native gas and USD for factory deploys before you confirm in wallet.',
    bottomCta: 'Read the full product overview and security notes in the docs.',
  },
  gasEstimator: {
    splitterDeployLabel: 'DivyFi splitter contract deploy: ',
  },
};

const pgsplitter: BrandConfig = {
  id: 'pgsplitter',
  daisyTheme: 'pgsplitter',
  storageNamespace: 'pgsplitter',
  displayName: 'pgSplitter',
  htmlTitle: 'pgSplitter',
  metaDescription:
    'pgSplitter — PulseChain & EVM revenue splits for the pgECO & $PULSEGUY ecosystem. Deploy, claim, and coordinate payees.',
  logoHeaderSrc: '/pgsplitter/pulseguy.jpg',
  logoHeaderAlt: 'pgSplitter · $PULSEGUY',
  heroLogoSrc: '/pgsplitter/pulseguy.jpg',
  heroLogoAlt: 'pgSplitter · $PULSEGUY',
  logoRoundedFull: true,
  wagmiAppName: 'pgSplitter',
  lifiIntegrator: 'pgSplitter',
  nav: {
    secondaryLabel: 'pgECO',
    secondaryHref: 'https://pulseguy.linkme.one',
  },
  docsNav: {
    label: 'Docs',
    href: 'https://docs.pgsplitter.xyz',
  },
  footer: {
    copyright: 'pgSplitter',
    linkHref: 'https://proofsec.xyz',
    linkLabel: 'proofsec.xyz',
    singlePartnerLine: {
      beforeLink: 'Partnered and audited by:',
      linkHref: 'https://proofsec.xyz',
      linkLabel: 'proofsec.xyz',
    },
  },
  dashboard: {
    heroTitle: 'pgSplitter',
    heroSubtitle:
      'pgSplitter is a web app for revenue sharing on PulseChain and major EVM networks. Deploy lightweight split contracts from a shared factory; each split specifies payees and their shares, holds native tokens and PRC/ERC-20 assets, and enables payees to claim their earnings automatically when funds arrive.',
    docLinkLabel: 'pgECO',
    connectedSubtitle: 'Your splits on PulseChain and other supported EVM networks.',
    marketingEvmsTitle: 'EVM factories',
    marketingEvmsBody:
      'PulseChain and multi-chain factories: find splitters where you are a payee, track native and ERC-20 balances, and claim your share.',
    marketingGasTitle: 'Gas & funding',
    marketingGasBody:
      'Estimate native gas and USD prices before you confirm in your wallet when you deploy on PulseChain or multiple chains.',
    bottomCta:
      'pgSplitter was built as part of the pgECO ecosystem. To explore DeFi tools from the pulseguy team:',
  },
  marketingGasCardFirst: true,
  libertySwapBanner: {
    href: 'https://libertyswap.finance/pulseguy',
    label: 'LibertySwap.finance/pulseguy',
  },
  ecosystemBlurb: {
    linkLabel: 'Click Here',
    linkHref: 'https://pulseguy.linkme.one',
  },
  gasEstimator: {
    splitterDeployLabel: 'Splitter contract deploy (gas units): ',
  },
  poweredBy: {
    href: 'https://divyfi.xyz',
    linkLabel: 'https://divyfi.xyz',
  },
  preferredChainId: 369,
};

const byId: Record<BrandId, BrandConfig> = {
  divyfi,
  pgsplitter,
};

export const brand = byId[brandId];

export function assetUrl(path: string): string {
  const base = process.env.PUBLIC_URL ?? '';
  return `${base}${path}`;
}

export const storageKeys = {
  deployedSplitsBase: `${brand.storageNamespace}:deployed-splits`,
  gasEstimatorChains: `${brand.storageNamespace}.gasEstimator.selectedChains`,
  erc20Splitter: (chainId: number, splitterLower: string) =>
    `${brand.storageNamespace}:erc20:${chainId}:${splitterLower}`,
  splitterLabel: (chainId: number, splitterLower: string) =>
    `${brand.storageNamespace}:splitter:label:${chainId}:${splitterLower}`,
};
